import { type Activity, findActivity, findVenue, getItem, getTown, minigameAt, type Quality, recipesAt, skill } from '@wyn/content';
import type { MinigameMove, SessionView, Settlement } from '@wyn/contracts';
import { featName, gameKey, type InputTrace, isRealtime, NEEDS_WARE, type RealtimeEngine, recipeXp, type TurnEngine, verifyReveal, wareValue } from '@wyn/game-core';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from '@tanstack/react-router';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { BAR_META } from '../components/BarMeter.tsx';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { Callout, PageTitle } from '../components/ui.tsx';
import { Icon } from '../components/Icon.tsx';
import { UI } from '../icons/ui-icons.ts';
import { minigameSessionQuery, useAction, useMinigame } from '../lib/queries.ts';
import { coin } from '../lib/time.ts';
import { useMe } from '../lib/useMe.ts';
import { LIVE_GAMES, settleDelayMs, TURN_GAMES } from '../minigames/registry.tsx';
import { Loading } from './Landing.tsx';

/** Let the end of a real-time game (the tree falling, the fish landing) play out before the result. */
const OUTRO_MS = 1400;

const randomHex = () => [...crypto.getRandomValues(new Uint8Array(16))].map((b) => b.toString(16).padStart(2, '0')).join('');

export function Venue() {
  const { venueKey } = useParams({ from: '/venue/$venueKey' });
  const me = useMe();
  const p = me.data?.player;
  const sessionQ = useQuery({ ...minigameSessionQuery(), enabled: !!p?.minigame });
  const mg = useMinigame();
  const [settlement, setSettlement] = useState<{ s: Settlement; session: SessionView } | null>(null);
  const [session, setSession] = useState<SessionView | null>(null);

  // Resume a game in progress after a reload.
  useEffect(() => {
    const live = sessionQ.data?.session;
    if (!session && live && live.venue === venueKey) setSession(live);
  }, [sessionQ.data, session, venueKey]);

  if (me.isPending) return <Loading />;
  if (!p) return <Navigate to="/" />;
  const venue = findVenue(venueKey);
  const game = minigameAt(venueKey);
  if (!venue || !game) return <Navigate to="/town" />;
  const sk = me.data!.skills.find((s) => s.skill === venue.skill);
  const level = sk?.level ?? 1;
  const here = p.town === venue.town && p.status.kind === 'idle';
  const elsewhere = p.minigame && p.minigame.venue !== venueKey ? findVenue(p.minigame.venue) : null;

  const [choosing, setChoosing] = useState<string | null>(null);
  const needsWare = NEEDS_WARE.includes(game.engine);
  const begin = (activity: string, ware?: { itemKey: string; quality: Quality }) => {
    setSettlement(null);
    if (needsWare && !ware) return setChoosing(activity);
    setChoosing(null);
    mg.start.mutate({ venue: venueKey, activity, ware }, { onSuccess: (r) => setSession(r.session) });
  };
  const settle = (s: Settlement, sess: SessionView) => {
    setSettlement({ s, session: sess });
    setSession(null);
  };

  return (
    <GameShell me={me.data!} skew={me.skew}>
      <div className="space-y-4">
        <section className="space-y-1">
          <Link to="/town" className="text-sm text-ink-faint hover:text-ink">← {getTown(venue.town).name}</Link>
          <PageTitle icon={venue.icon}>{venue.name}</PageTitle>
          <p className="text-ink-dim">{venue.description}</p>
          <div className="flex items-center gap-3 font-mono text-sm">
            <span>{skill(venue.skill).name} · Level {level}</span>
            {sk?.toNext && (
              <div className="h-1.5 flex-1 bg-rule" role="progressbar" aria-label={`${skill(venue.skill).name} XP`} aria-valuenow={sk.into} aria-valuemax={sk.toNext}>
                <div className="h-full bg-gilt" style={{ width: `${(sk.into / sk.toNext) * 100}%` }} />
              </div>
            )}
            {sk?.toNext && <span className="text-ink-faint">{sk.into}/{sk.toNext} XP</span>}
          </div>
        </section>

        {session ? (
          <Play
            key={session.id}
            session={session}
            onSettled={settle}
            onAbandon={() => mg.abandon.mutate(undefined, { onSuccess: () => setSession(null) })}
            abandoning={mg.abandon.isPending}
          />
        ) : settlement ? (
          <Result
            settlement={settlement.s}
            session={settlement.session}
            onAgain={here ? () => begin(settlement.s.activity) : undefined}
            starting={mg.start.isPending}
            onBack={() => setSettlement(null)}
          />
        ) : choosing ? (
          <WarePicker activity={findActivity(venueKey, choosing)!} onPick={(w) => begin(choosing, w)} onCancel={() => setChoosing(null)} busy={mg.start.isPending} error={mg.start.error} />
        ) : (
          <section className="panel space-y-3">
            {!here && <p className="text-sm text-ochre">You must be in {getTown(venue.town).name}, free and on your feet, to play here.</p>}
            {elsewhere && <p className="text-sm text-ochre">You have a game in progress at {elsewhere.name}. <Link to="/venue/$venueKey" params={{ venueKey: elsewhere.key }} className="text-gilt underline">Go back to it</Link>.</p>}
            <Grouped list={game.activities as Activity[]} render={(a) => {
                const locked = level < a.level;
                const bar = BAR_META[a.cost.bar];
                const stake = 'stake' in a ? a.stake : 0;
                const short = p.bars[a.cost.bar].value < a.cost.amount ? `Not enough ${bar.label}` : stake > p.purse ? 'Your purse is too light' : null;
                const played = p.record.games[gameKey(venueKey, a.key)];
                return (
                  <li key={a.key}>
                    <button
                      type="button"
                      aria-label={a.name}
                      disabled={!here || !!p.minigame || locked || !!short || mg.start.isPending}
                      onClick={() => begin(a.key)}
                      className="w-full border border-rule px-3 py-2 text-left hover:border-gilt disabled:opacity-45 disabled:hover:border-rule"
                    >
                      <div>{locked && <Icon name={UI.locked} className="mr-1 text-ink-faint" />}{a.name}</div>
                      {a.description && <div className="text-sm text-ink-dim">{a.description}</div>}
                      <div className="font-mono text-xs text-ink-faint">
                        Lv {a.level} · <Icon name={bar.icon} className="mr-0.5" />{a.cost.amount} {bar.label}{stake ? ` · stake ${coin(stake)}` : ''}
                        {played && <span className="text-ink-dim"> · best {pct(played.best)}</span>}
                      </div>
                      {!locked && short && <div className="text-xs text-ochre">{short}</div>}
                    </button>
                  </li>
                );
              }} />
            <ErrorNotice error={mg.start.error} />
          </section>
        )}
        {!session && !settlement && !choosing && <Recipes venueKey={venueKey} here={here} />}
      </div>
    </GameShell>
  );
}

/** A two-column list, split under headings by each entry's `group`; ungrouped entries come first. */
function Grouped<T extends { key: string; group?: string }>({ list, render }: { list: T[]; render: (x: T) => ReactNode }) {
  const groups = [...new Set(list.map((x) => x.group))];
  return (
    <div className="space-y-3">
      {groups.map((g) => (
        <div key={g ?? ''} className="space-y-1">
          {g && <h3 className="smallcaps text-sm text-ink-dim">{g}</h3>}
          <ul className="grid gap-2 sm:grid-cols-2">{list.filter((x) => x.group === g).map(render)}</ul>
        </div>
      ))}
    </div>
  );
}

/** Cross-skill recipes made here: no game, a fixed result. The worst goods go first and set the quality. */
function Recipes({ venueKey, here }: { venueKey: string; here: boolean }) {
  const me = useMe();
  const craft = useAction('craft', { refetch: ['me'] });
  const list = recipesAt(venueKey);
  if (list.length === 0) return null;
  const { player: p, skills, inventory } = me.data!;
  const levels = new Map(skills.map((s) => [s.skill, s.level]));
  const held = (itemKey: string) => inventory.filter((i) => i.itemKey === itemKey).reduce((n, i) => n + i.quantity, 0);
  const made = craft.data?.outcome;
  return (
    <section className="panel space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="heading">Recipes</h2>
        <span className="text-sm text-ink-faint">Work that takes more than one skill. Your worst goods go in first, and set the quality.</span>
      </div>
      <Grouped list={list} render={(r) => {
          const unskilled = r.skills.some((x) => (levels.get(x.skill) ?? 1) < x.level);
          const missing = r.inputs.some((i) => held(i.item) < i.quantity);
          const bar = BAR_META[r.cost.bar];
          const short = p!.bars[r.cost.bar].value < r.cost.amount;
          const product = getItem(r.product.item);
          return (
            <li key={r.key} className="space-y-1 border border-rule px-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <span><Icon name={product.icon} className="mr-2" />{r.name}</span>
                <span className="font-mono text-xs text-ink-faint">{r.product.quantity} × {product.name.toLowerCase()}</span>
              </div>
              <div className="text-sm text-ink-dim">{r.description}</div>
              <div className="font-mono text-xs">
                {r.skills.map((x) => (
                  <span key={x.skill} className={`mr-2 ${(levels.get(x.skill) ?? 1) >= x.level ? 'text-ink-faint' : 'text-ochre'}`}>{skill(x.skill).name} {x.level}</span>
                ))}
                <span className="text-ink-faint">· <Icon name={bar.icon} className="mr-0.5" />{r.cost.amount} {bar.label} · +{recipeXp(r)} XP each</span>
              </div>
              <div className="text-xs">
                {r.inputs.map((i) => (
                  <span key={i.item} className={`mr-2 ${held(i.item) >= i.quantity ? 'text-ink-faint' : 'text-ochre'}`}>
                    {i.quantity} × {getItem(i.item).name.toLowerCase()} ({held(i.item)})
                  </span>
                ))}
              </div>
              <button
                type="button"
                className="btn"
                aria-label={`Make ${r.name}`}
                disabled={!here || !!p!.minigame || unskilled || missing || short || craft.isPending}
                onClick={() => craft.mutate({ recipe: r.key })}
              >
                Make
              </button>
            </li>
          );
        }} />
      {made && !craft.isPending && (
        <Callout tone="good">
          You make {made.product.quantity} × {made.product.quality} {getItem(made.product.itemKey).name.toLowerCase()}
          {made.spared ? ', and thrift spares your materials' : ''}.
        </Callout>
      )}
      <ErrorNotice error={craft.error} />
    </section>
  );
}

function Play({ session, onSettled, onAbandon, abandoning }: { session: SessionView; onSettled: (s: Settlement, v: SessionView) => void; onAbandon: () => void; abandoning: boolean }) {
  const mg = useMinigame();
  const [view, setView] = useState(session);
  const clientSeed = useRef(randomHex());
  const activity = findActivity(session.venue, session.activity)!;
  const [ending, setEnding] = useState(false);

  const move = (m: MinigameMove) =>
    mg.move.mutate(
      { sessionId: session.id, move: m, ...(view.step === 0 ? { clientSeed: clientSeed.current } : {}) },
      {
        onSuccess: (r) => {
          setView(r.session);
          // Let the last dig or the house's last roll sink in before the result.
          if (r.settlement) setTimeout(() => onSettled(r.settlement!, r.session), settleDelayMs(r.session));
        },
      },
    );
  const finish = (trace: InputTrace) => {
    setEnding(true);
    const started = Date.now();
    mg.finish.mutate(
      { sessionId: session.id, trace },
      { onSuccess: (r) => setTimeout(() => onSettled(r.settlement, session), Math.max(0, OUTRO_MS - (Date.now() - started))) },
    );
  };

  return (
    <section className="panel space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="heading">{activity.name}</h2>
        {ending && !mg.finish.error && <span className="text-sm text-ink-faint">The judges confer…</span>}
      </div>
      {isRealtime(view.engine) ? <LiveGame session={view} activity={activity} onDone={finish} /> : <TurnGame session={view} busy={mg.move.isPending} onMove={move} />}
      <ErrorNotice error={mg.move.error ?? mg.finish.error} />
      <div className="text-right">
        <button type="button" className="text-xs text-ink-faint underline hover:text-ink" disabled={abandoning} onClick={onAbandon}>
          Abandon (what you paid to sit down is lost)
        </button>
      </div>
    </section>
  );
}

/** For venues that trade: which of your goods to bring to the table, with what a trader reckons each is worth. */
function WarePicker({ activity, onPick, onCancel, busy, error }: { activity: Activity; onPick: (w: { itemKey: string; quality: Quality }) => void; onCancel: () => void; busy: boolean; error: unknown }) {
  const me = useMe();
  const goods = me.data!.inventory
    .filter((i) => getItem(i.itemKey).value)
    .map((i) => ({ ...i, worth: wareValue(i.itemKey, i.quality) }))
    .sort((a, b) => b.worth - a.worth);
  return (
    <section className="panel space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="heading">{activity.name}: what will you sell?</h2>
        <button type="button" className="btn-quiet" onClick={onCancel}>Back</button>
      </div>
      {goods.length === 0 ? (
        <p className="text-sm text-ink-faint">You have nothing a trader would want. Make, catch or win something first.</p>
      ) : (
        <ul className="grid gap-2 sm:grid-cols-2">
          {goods.map((g) => (
            <li key={`${g.itemKey}#${g.quality}`}>
              <button type="button" disabled={busy} onClick={() => onPick({ itemKey: g.itemKey, quality: g.quality })} className="w-full border border-rule px-3 py-2 text-left hover:border-gilt disabled:opacity-45">
                <div><span className="capitalize">{g.quality}</span> {getItem(g.itemKey).name.toLowerCase()} <span className="text-ink-faint">×{g.quantity}</span></div>
                <div className="font-mono text-xs text-ink-faint">Worth {coin(g.worth)}</div>
              </button>
            </li>
          ))}
        </ul>
      )}
      <ErrorNotice error={error} />
    </section>
  );
}

function LiveGame({ session, activity, onDone }: { session: SessionView; activity: Activity; onDone: (t: InputTrace) => void }) {
  const Game = LIVE_GAMES[session.engine as RealtimeEngine];
  return <Game session={session} activity={activity} onDone={onDone} />;
}

function TurnGame({ session, busy, onMove }: { session: SessionView; busy: boolean; onMove: (m: MinigameMove) => void }) {
  const Game = TURN_GAMES[session.engine as TurnEngine];
  return <Game session={session} view={session.view} busy={busy} onMove={onMove} />;
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

function Result({ settlement: s, session, onAgain, starting, onBack }: { settlement: Settlement; session: SessionView; onAgain?: () => void; starting: boolean; onBack: () => void }) {
  // Only a score that beats one on record is a new best: the first play has nothing to beat.
  const newBest = s.best !== null && s.score > s.best;
  const fair = s.reveal ? verifyReveal({ venue: s.venue, activity: s.activity, level: session.level, commitment: session.commitment, reveal: s.reveal, ware: session.ware }).ok : null;
  return (
    <section className="panel space-y-3" aria-label="Result">
      {s.yield ? (
        <div>
          <div className="text-sm text-ink-dim">{s.title}</div>
          <div className="text-2xl"><span className="capitalize">{s.yield.quality}</span> {getItem(s.yield.itemKey).name.toLowerCase()}{s.yield.quantity > 1 ? ` × ${s.yield.quantity}` : ''}</div>
        </div>
      ) : (
        <div className="text-2xl">{s.title}</div>
      )}
      <p className="text-ink-dim">{s.text}</p>
      <p className="font-mono text-sm">
        {s.coin > 0 && <span className="mr-3 text-gilt">+{coin(s.coin)}</span>}
        <span className="text-verdigris">+{s.xp} {skill(s.skill).name} XP</span>
        {s.renown > 0 && <span className="ml-3 text-ink-dim">+{s.renown} Renown</span>}
      </p>
      {s.level > s.levelBefore && <p className="text-gilt"><Icon name={UI.levelUp} className="mr-1" />Your {skill(s.skill).name} is now level {s.level}.</p>}
      {newBest && <p className="text-gilt">A new personal best: {pct(s.score)}, beating {pct(s.best!)}.</p>}
      {s.feats.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Feats">
          {s.feats.map((f) => <li key={f} className="border border-gilt px-2 py-0.5 text-sm text-gilt">{featName(session.engine, f)}</li>)}
        </ul>
      )}
      {s.breakdown.length > 0 && (
        <div className="space-y-2">
          {s.breakdown.map(([label, v]) => (
            <div key={label} className="text-sm">
              {label} <span className="text-ink-faint">{pct(v)}</span>
              <div className="h-1.5 bg-rule"><div className="h-full bg-gilt" style={{ width: `${Math.min(100, v * 100)}%` }} /></div>
            </div>
          ))}
        </div>
      )}
      {fair !== null && (
        <p className={`break-all font-mono text-xs ${fair ? 'text-verdigris' : 'text-blood'}`}>
          {fair ? '✓ Fair: the revealed seed matches the commitment and replays every move.' : '✗ The revealed seed does not match the commitment.'} Seed {s.reveal!.serverSeed}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {onAgain && <button type="button" className="btn" disabled={starting} onClick={onAgain}>Again</button>}
        <button type="button" className="btn-quiet" onClick={onBack}>Back</button>
      </div>
    </section>
  );
}
